
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PRODUCTS } from '../constants';

const Home: React.FC = () => {
  const navigate = useNavigate();

  const handleSelectKit = (kitId: string) => {
    const currentKit = localStorage.getItem('growLocalSelectedKit');
    
    // If selecting a different kit than what's currently active, 
    // we clear progress to avoid task and quiz score contamination 
    // across different curriculums.
    if (currentKit && currentKit !== kitId) {
      localStorage.removeItem('growLocalProgress');
      localStorage.removeItem('growLocalActiveStage');
    }
    
    localStorage.setItem('growLocalSelectedKit', kitId);
    navigate('/learn');
  };

  return (
    <div className="space-y-32 pb-32">
      {/* Landscape Banner Hero Section */}
      <section className="relative w-full min-h-[90vh] flex items-center overflow-hidden">
        {/* Banner Image Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2?auto=format&fit=crop&q=80&w=2000" 
            alt="Sustainable Urban Gardening" 
            className="w-full h-full object-cover"
          />
          {/* Refined Gradient Overlay for High Text Distinguishability */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/60 to-transparent"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-24">
          <div className="max-w-5xl flex flex-col items-start gap-2 animate-in fade-in slide-in-from-left-8 duration-1000">
            <h1 className="text-[5rem] md:text-[11rem] font-black leading-[0.85] tracking-tighter serif-heading drop-shadow-2xl !text-white m-0 p-0 text-left">
              <span className="italic">Grow Local</span>
            </h1>
            
            <p className="text-2xl md:text-4xl text-white font-black leading-tight tracking-tight text-left m-0 p-0 max-w-2xl opacity-90 pl-4 md:pl-10">
              Learn as you Grow
            </p>
            
            <div className="flex flex-wrap items-center gap-6 pt-12">
              <Link to="/learn" className="btn-primary !bg-grow-green hover:opacity-90 text-lg !px-10 !py-4 shadow-2xl">
                Explore Curriculum
              </Link>
              <a 
                href="https://gofund.me/bbb5db69d" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary !bg-grow-green hover:opacity-90 text-lg !px-10 !py-4 shadow-2xl flex items-center gap-2 group"
              >
                Donate Now <span className="text-2xl transform group-hover:translate-x-2 transition-transform">→</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Decorative Edge */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#f0fdf4] to-transparent pointer-events-none"></div>
      </section>

      {/* Centered "What we do" Section */}
      <section className="max-w-7xl mx-auto px-6 text-center">
        <div className="space-y-8 flex flex-col items-center">
          <div className="space-y-4">
            <h2 className="text-5xl font-black serif-heading">What we do</h2>
          </div>
          <div className="max-w-4xl">
            <p className="text-xl md:text-2xl text-emerald-900/70 leading-relaxed font-medium">
              Grow Local is an all-in-one home gardening kit designed to make growing food simple, fun, and accessible for everyone. Our kits combine hands-on gardening with digital learning to help beginners build confidence while learning how plants grow from seed to stem.
            </p>
          </div>
        </div>
      </section>

      {/* Refined Product Grid */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col items-center text-center mb-16 gap-6">
          <div className="space-y-4 max-w-2xl">
            <h2 className="text-5xl font-black serif-heading">Our Kits</h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-10">
          {PRODUCTS.map((product) => (
            <div key={product.id} className="modern-card group flex flex-col shadow-sm hover:shadow-xl">
              <div className="aspect-[16/9] overflow-hidden rounded-t-[24px]">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="p-10 flex-1 flex flex-col justify-between">
                <div className="space-y-4">
                  <h3 className="text-3xl font-black serif-heading">{product.name}</h3>
                  <p className="text-emerald-900/70 leading-relaxed font-medium">{product.description}</p>
                </div>
                <div className="pt-8">
                  <button 
                    onClick={() => handleSelectKit(product.id)}
                    className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl bg-emerald-900 text-white font-bold hover:bg-emerald-700 transition-all shadow-lg hover:shadow-emerald-900/20"
                  >
                    View Curriculum
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
